import { Container, Graphics, Sprite, Text } from "pixi.js"
import { stageViewport } from "../../../../canvas_layout"
import { IntroBackground } from "./intro_background"
import { GuiManager } from "../gui_manager"
import { ProjectLogo } from "./project_logo"
import { randomIntBetween } from "game-common/models"
import { BasicSprite } from "../sprites/basic_sprite"
import { TextRenderer } from "../text/text_renderer"
import { trackLetters } from "../text/typography"
import { LoginCard, LoginMode } from "./login_card"
import { COLORS, INTRO_FONT, METRICS, SCREEN_H, SCREEN_W, drawChip, ensureIntroFont } from "./intro_theme"
import { Tween, tween } from "./intro_motion"
import { EmberDrift, nightfallTexture, softGlowTexture, vignetteTexture } from "../gui/ember_atmosphere"
import {
    enterFullscreenIfMobile,
    fullscreenSupported,
    shouldOfferHomeScreenInstall,
    toggleFullscreen,
} from "../../../../fullscreen"

type LoginCallback = (username?: string, password?: string) => void

/** Where the wordmark starts before it settles, and how long the slide takes. */
const LOGO_DROP = 120
const LOGO_SLIDE_MS = 630

/**
 * The card's middle. The wordmark (and the tagline under it) settle above ~150 and the
 * socials/fullscreen chips sit at CHROME_Y, so this centres the card in what is left and lets it grow
 * either way.
 */
const CARD_CENTER_Y = 281
const CHROME_Y = SCREEN_H - 40

/** The height of the chips on the chrome row, which the install hint is centred against. */
const CHIP_H = 34

/**
 * What an iPhone gets instead of the expand chip. Worth the words: nothing else on the page can
 * put the game edge to edge there, and the player has no reason to guess that the browser's own
 * Share menu is where the fullscreen button went.
 */
const INSTALL_HINT = "Tap Share, then Add to Home Screen, for fullscreen"

/**
 * One line under the wordmark: what the game is about, in the fewest words. It names the loop the
 * player is about to live in -- the fire in the dark -- rather than describing the product.
 */
const TAGLINE = "KEEP THE FIRE ALIVE"
const TAGLINE_GAP = 11

/** How long the embers gust when Play is pressed. */
const SEND_OFF_MS = 1400

/** Fog banks: slow, dark, and drifting a few dozen pixels either way over most of a minute. */
const FOG = [
    { x: 0.18, y: 0.55, w: 0.7, h: 0.26, period: 31000, reach: 46, seed: 0 },
    { x: 0.62, y: 0.78, w: 0.75, h: 0.24, period: 43000, reach: 60, seed: 2.1 },
    { x: 0.86, y: 0.42, w: 0.6, h: 0.22, period: 37000, reach: 38, seed: 4.3 },
]

/**
 * The light over the login screen: night coming down on the forest, fog, and embers rising past the
 * card. Owns its own animation frame loop, and stops it in `dispose`.
 */
class IntroAtmosphere extends Container {
    private shade = new Sprite(nightfallTexture())
    private vignette = new Sprite(vignetteTexture())
    private fog: Sprite[] = []
    private embers: EmberDrift
    private w = SCREEN_W
    private h = SCREEN_H + 40
    private startedAt = Date.now()
    private last = Date.now()
    private frame?: number

    constructor() {
        super()
        this.addChild(this.shade)
        FOG.forEach(() => {
            const bank = new Sprite(softGlowTexture())
            bank.anchor.set(0.5)
            bank.tint = 0x3a3028
            bank.alpha = 0.32
            this.fog.push(bank)
            this.addChild(bank)
        })
        this.addChild(this.vignette)
        // Over the vignette: an ember is a light, and the dark rim should not swallow it.
        this.embers = new EmberDrift({
            width: this.w,
            height: this.h,
            count: 34,
            spawnBand: 0.3,
            minSize: 4,
            maxSize: 9,
            minRise: 16,
            maxRise: 38,
            sway: 18,
            minLife: 4,
            maxLife: 9,
        })
        this.addChild(this.embers)
        this.layout()
        this.tick()
    }

    resize = (width: number, height: number) => {
        this.w = width
        this.h = height
        this.embers.resize(width, height)
        this.layout()
    }

    /** Play was pressed: the embers gust up past the card as it goes. */
    sendOff = () => {
        this.embers.surge(SEND_OFF_MS)
    }

    dispose = () => {
        if (this.frame !== undefined && typeof cancelAnimationFrame === "function") {
            cancelAnimationFrame(this.frame)
        }
        this.frame = undefined
        this.removeChildren()
    }

    private layout = () => {
        const { w, h } = this
        this.shade.width = w
        this.shade.height = h
        this.vignette.width = w
        this.vignette.height = h
        this.paint(Date.now() - this.startedAt)
    }

    private tick = () => {
        const now = Date.now()
        this.embers.update(now - this.last)
        this.last = now
        this.paint(now - this.startedAt)
        if (typeof requestAnimationFrame === "function") {
            this.frame = requestAnimationFrame(this.tick)
        }
    }

    private paint = (ms: number) => {
        const { w, h } = this
        FOG.forEach((bank, i) => {
            const sprite = this.fog[i]
            sprite.x = w * bank.x + bank.reach * Math.sin((2 * Math.PI * ms) / bank.period + bank.seed)
            sprite.y = h * bank.y
            sprite.width = w * bank.w
            sprite.height = h * bank.h
        })
    }
}

export class Intro extends Graphics {
    guiManager: GuiManager
    loggingIn: boolean
    landingContainer: Graphics
    container: Container
    socials: Graphics

    private card?: LoginCard
    private logo?: ProjectLogo
    private background?: Graphics
    private atmosphere?: IntroAtmosphere
    private tagline?: Text
    private taglineMotion?: Tween
    private exitMotion?: Tween

    constructor(guiManager: GuiManager) {
        super()
        this.guiManager = guiManager
    }

    formRemover = () => {
        this.exitMotion?.cancel()
        this.card?.dispose()
        this.card = undefined
        this.container?.removeChildren()
        this.container?.parent?.removeChild(this.container)
        this.container = undefined
    }

    guestLandingRemover = () => {
        window.removeEventListener("samsara:canvas-resize", this.resize)
        this.formRemover()

        // Frees the wordmark's canvas rasters and stops its animation frame loop; neither is
        // reclaimed by removeChildren().
        this.logo?.dispose()
        this.logo = undefined

        // Same for the atmosphere's loop. Its textures are shared and cached, so they stay.
        this.atmosphere?.dispose()
        this.atmosphere = undefined
        this.taglineMotion?.cancel()
        this.tagline?.destroy({ texture: true, baseTexture: true })
        this.tagline = undefined

        this.landingContainer?.removeChildren()
        this.landingContainer?.parent?.removeChild(this.landingContainer)

        this.socials?.removeChildren()
        this.socials?.parent?.removeChild(this.socials)
    }

    /**
     * Show or hide the login form.
     *
     * Not just `container.visible`: the card's fields are real DOM `<input>`s that only follow
     * pixi's visibility from inside a render pass, so hiding the container alone leaves them --
     * and the "Pick a name, or play as a guest" placeholder -- on the page, over whatever screen
     * comes next, until the card is finally disposed. See LoginField.setDomVisible.
     */
    private setFormVisible = (visible: boolean) => {
        if (visible) {
            // Undo a send-off that was in flight: a rejected sign-in brings the card straight back.
            this.exitMotion?.cancel()
            this.exitMotion = undefined
            if (this.container) {
                this.container.alpha = 1
                this.container.pivot.y = 0
            }
        }
        if (this.container) {
            this.container.visible = visible
        }
        this.card?.setDomVisible(visible)
    }

    /**
     * The card goes: the form sinks and fades while the embers gust up past where it was. The DOM fields leave at once -- a real `<input>` cannot
     * fade with the canvas, and one lingering over the next screen is the ghost-placeholder bug.
     */
    private sendOff = () => {
        this.card?.setDomVisible(false)
        this.atmosphere?.sendOff()
        const container = this.container
        if (!container) {
            return
        }
        this.exitMotion?.cancel()
        this.exitMotion = tween(
            240,
            t => {
                container.alpha = 1 - t
                container.pivot.y = -10 * t
            },
            { onDone: () => (container.visible = false) },
        )
    }

    loginSuccess = () => {
        this.loggingIn = false
        this.guestLandingRemover()
    }

    /**
     * A rejected sign-in. Returns true when the card took the message inline -- the caller then
     * skips the global screen banner, which used to land across the wordmark.
     */
    loginFailed = (): boolean => {
        this.loggingIn = false

        if (this.card) {
            this.setFormVisible(true)
            this.card.showError("That name and password don't match.")
            return true
        }

        this.setFormVisible(true)
        return false
    }

    init = (doneCallback: LoginCallback) => {
        if (this.loggingIn) {
            return
        }

        // Jost has to be resolvable before the first Text is built; a pixi Text keeps whatever face
        // it rasterized with. ensureIntroFont never rejects, so a slow CDN-less start still gets a
        // login screen, just in Verdana.
        ensureIntroFont().then(() => this.build(doneCallback))
    }

    private build = (doneCallback: LoginCallback) => {
        const landingContainer = new Graphics()
        this.landingContainer = landingContainer

        const introBackgroundGenerator = new IntroBackground()
        const background = introBackgroundGenerator.update()
        this.background = background
        // The HTML loading scene fades onto this art while the wordmark settles.
        background.alpha = 1
        background.x = 0
        landingContainer.addChild(background)

        // Nightfall, fog and embers -- and the scrim that used to keep white text legible over a
        // sunlit canopy, which the dark now does on its own.
        const atmosphere = new IntroAtmosphere()
        this.atmosphere = atmosphere
        landingContainer.addChild(atmosphere)

        const introGenerator = new ProjectLogo()
        this.logo = introGenerator
        const intro = introGenerator.update(() => {
            introGenerator.pulse()
            this.slideLogo(intro)
            this.revealTagline()

            this.container = this.buildLoginContainer(doneCallback)
            this.setFormVisible(false)
            this.guiManager.stage.addChild(this.container)

            this.socials = this.buildSocials(this.guiManager.stage)
            this.resize()

            this.setFormVisible(true)
            this.card?.playEntrance()
        })

        intro.x = Math.round((SCREEN_W - introGenerator.calcWidth()) / 2)
        intro.y = LOGO_DROP

        this.tagline = this.buildTagline(introGenerator)
        intro.addChild(this.tagline)

        landingContainer.addChild(intro)
        this.guiManager.stage.addChild(landingContainer)
        this.resize()
        window.addEventListener("samsara:canvas-resize", this.resize)
        requestAnimationFrame(() => window["stopLoading"]?.())
    }

    /** Resize in place so the logo animation and entered credentials survive orientation changes. */
    private resize = () => {
        const viewport = stageViewport()
        const coverHeight = Math.max(SCREEN_H + 40, viewport.height)
        if (this.background) {
            const art = this.background.children[0] as Sprite
            const expanded = viewport.inset > 0 || viewport.insetY > 0
            const scale = expanded
                ? Math.max(viewport.width / art.texture.width, coverHeight / art.texture.height)
                : 0.3
            art.scale.set(scale)
            art.x = expanded ? (viewport.width - art.width) / 2 : 0
            art.y = expanded ? (coverHeight - art.height) / 2 : 0
            this.background.x = viewport.left
            this.background.y = viewport.top
        }
        if (this.atmosphere) {
            this.atmosphere.x = viewport.left
            this.atmosphere.y = viewport.top
            this.atmosphere.resize(viewport.width, coverHeight)
        }
        if (this.card) {
            // Keep the desktop card proportions; only the backdrop and edge chrome expand.
            this.card.x = Math.round((SCREEN_W - METRICS.cardWidth) / 2)
        }
        const socials = this.socials?.getChildByName("socials")
        if (socials) {
            socials.x = viewport.left + 12
            socials.y = CHROME_Y + viewport.insetY
        }
        const chrome = this.socials?.getChildByName("rightChrome") as Container | undefined
        if (chrome) {
            chrome.x = viewport.right - 12 - chrome.width
            chrome.y = CHROME_Y + viewport.insetY + (CHIP_H - chrome.height) / 2
        }
    }

    /**
     * Ease the wordmark up to its resting place (the old version stepped 1px on a 1ms timer).
     *
     * Expo rather than cubic: it covers most of the distance early and then creeps the last few
     * pixels, which reads as the block settling into place instead of coasting to a stop. No
     * overshoot here -- the wordmark rests at y=0, the top of the screen, so there is nothing above
     * it to spring into. The spring lives in the individual letters, where there is room for it.
     *
     * LOGO_SLIDE_MS is the one dial for how gradual this feels; it is the whole travel time.
     */
    private slideLogo = (intro: Container) => {
        const started = Date.now()
        const tick = () => {
            const t = Math.min(1, (Date.now() - started) / LOGO_SLIDE_MS)
            const eased = t >= 1 ? 1 : 1 - Math.pow(2, -10 * t)
            intro.y = Math.round(LOGO_DROP * (1 - eased))
            if (t < 1) {
                requestAnimationFrame(tick)
            }
        }
        requestAnimationFrame(tick)
    }

    /**
     * The tagline hangs under the wordmark's hairline, inside the wordmark's own container so it rides
     * the slide up with it. Built hidden; `revealTagline` brings it in once the hairline has drawn.
     */
    private buildTagline = (logo: ProjectLogo): Text => {
        const tagline = new TextRenderer()
            .fontFamily(INTRO_FONT)
            .fontSize(11)
            .strokeThickness(0)
            .color(COLORS.muted)
            .render(trackLetters(TAGLINE)) as Text
        tagline.style.fontWeight = "500"
        tagline.x = Math.round((logo.calcWidth() - tagline.width) / 2)
        tagline.y = Math.round(logo.ruleY() + TAGLINE_GAP)
        tagline.alpha = 0
        return tagline
    }

    private revealTagline = () => {
        const tagline = this.tagline
        if (!tagline) {
            return
        }
        this.taglineMotion?.cancel()
        this.taglineMotion = tween(
            900,
            t => {
                tagline.alpha = 0.9 * t
                tagline.pivot.y = -5 * (1 - t)
            },
            { delay: 380 },
        )
    }

    /** The sign-in card is taller and grows over the tagline; let it step aside while it does. */
    private onCardMode = (mode: LoginMode) => {
        const tagline = this.tagline
        if (!tagline) {
            return
        }
        this.taglineMotion?.cancel()
        const from = tagline.alpha
        const to = mode === "account" ? 0 : 0.9
        this.taglineMotion = tween(220, t => (tagline.alpha = from + (to - from) * t))
    }

    private buildLoginContainer = (doneCallback: LoginCallback): Container => {
        const container = new Container()

        const guestName = "Guest " + randomIntBetween(1, 10000)

        const card = new LoginCard({
            guestName,
            centerY: CARD_CENTER_Y,
            onModeChange: this.onCardMode,
            onPlay: (playerName: string) => {
                // A phone can only expand from inside a gesture, and this is the first one that
                // means "I am playing" -- the taps before it may be aimed at the name field, and
                // resizing the page out from under an opening soft keyboard is its own mess.
                enterFullscreenIfMobile()
                this.sendOff()
                setTimeout(() => doneCallback(playerName), 250)
            },
            onLogin: (username: string, password: string) => {
                enterFullscreenIfMobile()
                this.loggingIn = true
                this.sendOff()
                doneCallback(username, password)
            },
        })

        // y is the card's own business -- it re-centres itself on CARD_CENTER_Y after each layout.
        card.x = Math.round((SCREEN_W - METRICS.cardWidth) / 2)

        container.addChild(card)
        this.card = card

        return container
    }

    buildSocials = (parent?: Container): Graphics => {
        const allContainer = new Graphics()
        if (parent) {
            parent.addChild(allContainer)
        }

        const socialContainer = new Graphics()
        socialContainer.name = "socials"
        socialContainer.x = 12
        socialContainer.y = CHROME_Y

        const socialMedia = this.guiManager.socialMedia()
        // socialMedia() positions its own row for the in-game HUD; the intro parks it in a chip.
        socialMedia.y = 0
        socialMedia.x = 0

        drawChip(socialContainer, socialMedia.width + 20, 34)
        socialMedia.x = 10
        socialMedia.y = Math.round((34 - socialMedia.height) / 2)
        socialContainer.addChild(socialMedia)
        allContainer.addChild(socialContainer)

        // The chrome arrives after the card, quietly: it is furniture, not part of the welcome.
        allContainer.alpha = 0
        tween(600, t => (allContainer.alpha = t), { delay: 700 })

        // No chip where the platform has no fullscreen to give: iPhone, and an embed framed
        // without allowfullscreen. Better an absent control than one that visibly does nothing --
        // but a phone in that position still has Add to Home Screen, so say so rather than leave
        // the corner empty.
        if (!fullscreenSupported()) {
            if (shouldOfferHomeScreenInstall()) {
                allContainer.addChild(this.buildInstallHint())
            }
            return allContainer
        }

        const fullscreenChip = new Graphics()
        fullscreenChip.name = "rightChrome"
        drawChip(fullscreenChip, CHIP_H, CHIP_H)
        fullscreenChip.x = SCREEN_W - CHIP_H - 12
        fullscreenChip.y = CHROME_Y

        const expandFullScreen = new BasicSprite({ name: "fullscreen.png", scale: 0.55 })
        expandFullScreen.x = 17
        expandFullScreen.y = 17
        fullscreenChip.addChild(expandFullScreen)

        fullscreenChip.interactive = true
        fullscreenChip.cursor = "pointer"
        fullscreenChip.on("click", toggleFullscreen)
        fullscreenChip.on("tap", toggleFullscreen)

        allContainer.addChild(fullscreenChip)

        return allContainer
    }

    /**
     * Plain muted type rather than a chip: a chip reads as a button, and there is nothing here to
     * press -- the control it names belongs to the browser, not to us.
     */
    private buildInstallHint = (): Text => {
        const hint = new TextRenderer()
            .fontFamily(INTRO_FONT)
            .role("caption").bold(false)
            .color(COLORS.muted)
            .render(INSTALL_HINT) as Text

        hint.x = Math.round(SCREEN_W - 12 - hint.width)
        hint.name = "rightChrome"
        hint.y = Math.round(CHROME_Y + (CHIP_H - hint.height) / 2)
        return hint
    }
}
